import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as ReactSelectBaseComponents } from 'react-select';
import AsyncSelect from 'react-select/async';

import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import classes from '@/inspectionAttach/InspectionSearch.module.css';

const Option = ({ children, innerProps, innerRef, isFocused, isSelected }) => (
  <div
    ref={innerRef}
    className={`${classes.option} ${isFocused ? classes.optionFocused : ''} ${isSelected ? classes.optionSelected : ''}`}
    {...innerProps}
  >
    {children}
  </div>
);

const LoadingMessage = ({ getStyles, innerProps, ...props }) => (
  <div {...innerProps} style={getStyles('loadingMessage', props)}>
    <FormattedMessage id="loading" />
  </div>
);

const NoOptionsMessage = ({ getStyles, innerProps, ...props }) => (
  <div {...innerProps} style={getStyles('loadingMessage', props)}>
    <FormattedMessage id="noOption" />
  </div>
);

const components = {
  Option,
  LoadingMessage,
  NoOptionsMessage,
  Input: inputProps => <ReactSelectBaseComponents.Input {...inputProps} data-cy="inspection-search-select" />,
};

const InspectionSearch = ({ apiUrl, handleSelect, searchPlaceholder }) => {
  const [value, setValue] = useState('');

  const onChange = selectValue => {
    setValue(selectValue);
    handleSelect(selectValue);
  };

  const handleFetchSuggestions = (inputValue, callback) => {
    if (!inputValue) callback([]);

    API.get(apiUrl + inputValue).then(({ data }) => {
      const mappedSuggestions = data?.map(suggestion => {
        const label = suggestion?.code ? `${suggestion?.identifier} (${suggestion?.code})` : suggestion?.identifier;

        return {
          value: suggestion?.id,
          label,
          ...suggestion,
        };
      });

      callback(mappedSuggestions || []);
    });
  };

  const handleFetchSuggestionsDebounced = debounce(handleFetchSuggestions, 500);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      height: '38px',
      borderRadius: '4px',
      borderColor: state.isFocused ? '#80bdff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '14px',
      lineHeight: 'normal',
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 8px',
      flexWrap: 'nowrap',
    }),
    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
      background: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
      '& input': {
        height: '100% !important',
        background: 'transparent !important',
        border: 'none !important',
        boxShadow: 'none !important',
        padding: '0 !important',
        margin: '0 !important',
      },
    }),
    singleValue: base => ({
      ...base,
      margin: 0,
      color: '#212529',
      fontWeight: 'bold',
    }),
    placeholder: base => ({
      ...base,
      margin: 0,
      color: '#6c757d',
    }),
    indicatorsContainer: base => ({
      ...base,
      height: '38px',
    }),
  };

  return (
    <div className={classes.Search}>
      <AsyncSelect
        value={value}
        placeholder={searchPlaceholder}
        components={components}
        loadOptions={handleFetchSuggestionsDebounced}
        onChange={onChange}
        styles={selectStyles}
      />
    </div>
  );
};

Option.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.func,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
};

LoadingMessage.propTypes = {
  getStyles: PropTypes.func,
  innerProps: PropTypes.object,
};

NoOptionsMessage.propTypes = {
  getStyles: PropTypes.func,
  innerProps: PropTypes.object,
};

InspectionSearch.propTypes = {
  handleSelect: PropTypes.func.isRequired,
  apiUrl: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string,
};

export default InspectionSearch;
